import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
  School, BookOpen, Target, ClipboardList, Flame, Grid3x3, Trophy, Swords,
  Crown, Sparkles, Star, ChevronRight, ArrowRight, Bell,
} from 'lucide-react';

// Accesos del alumno — mismas rutas y permisos que menus.student en AppLayout
// (fuente de verdad del sidebar). No introduce rutas ni permisos nuevos.
const TOOLS = [
  { key: 'clases',    to: '/student/classrooms',   label: 'Mis clases', desc: 'Únete a la clase en vivo',     icon: School,      tile: 'bg-amber-100 text-amber-700',     glow: 'group-hover:shadow-amber-200/60' },
  { key: 'cursos',    to: '/student/courses',      label: 'Cursos',     desc: 'Sigue aprendiendo ajedrez',    icon: BookOpen,    tile: 'bg-blue-100 text-blue-700',       glow: 'group-hover:shadow-blue-200/60' },
  { key: 'quizzes',   to: '/student/quizzes',      label: 'Quizzes',    desc: 'Pon a prueba tu ingenio',      icon: Target,      tile: 'bg-amber-100 text-amber-700',     glow: 'group-hover:shadow-amber-200/60' },
  { key: 'tareas',    to: '/student/assignments',  label: 'Tareas',     desc: 'Tus deberes pendientes',       icon: ClipboardList, tile: 'bg-blue-100 text-blue-700',     glow: 'group-hover:shadow-blue-200/60' },
  { key: 'desafios',  to: '/student/challenges',   label: 'Desafíos',   desc: 'Retos para ganar puntos',      icon: Flame,       tile: 'bg-orange-100 text-orange-700',   glow: 'group-hover:shadow-orange-200/60' },
  { key: 'tablero',   to: '/student/chess',        label: 'Tablero',    desc: 'Practica libremente',          icon: Grid3x3,     tile: 'bg-emerald-100 text-emerald-700', glow: 'group-hover:shadow-emerald-200/60' },
  { key: 'logros',    to: '/student/achievements', label: 'Logros',     desc: 'Tus medallas y trofeos',       icon: Trophy,      tile: 'bg-amber-100 text-amber-700',     glow: 'group-hover:shadow-amber-200/60' },
  { key: 'lichess',   to: '/student/lichess',      label: 'Lichess',    desc: 'Juega en línea',               icon: Swords,      tile: 'bg-violet-100 text-violet-700',   glow: 'group-hover:shadow-violet-200/60' },
];

export default function StudentDashboard() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [notes, setNotes] = useState([]);
  const [achs, setAchs] = useState([]);
  const [courses, setCourses] = useState([]);
  const [mounted, setMounted] = useState(false);
  const [lichess, setLichess] = useState(null);

  useEffect(() => {
    setMounted(true);
    api.get('/auth/me').then(r => setMe(r.data)).catch(() => {});
    api.get('/assignments').then(r => setAssignments(r.data.slice(0, 3))).catch(() => {});
    api.get('/notifications').then(r => setNotes(r.data.slice(0, 4))).catch(() => {});
    api.get('/achievements/mine').then(r => setAchs(r.data.slice(0, 4))).catch(() => {});
    api.get('/courses').then(r => setCourses(r.data.slice(0, 3))).catch(() => {});
    api.get('/lichess/me').then(r => setLichess(r.data)).catch(() => {});
  }, []);

  const s = me?.student;
  const level = s?.gamification_level || 1;
  const points = s?.points || 0;
  const nextLevelPts = level * 500;
  const progress = Math.min(100, ((points % 500) / 500) * 100);
  const lichessPerfs = lichess?.lichess_data?.perfs || {};
  const lichessRating = lichessPerfs.blitz?.rating || lichessPerfs.rapid?.rating || null;
  const lichessRatingType = lichessPerfs.blitz?.rating ? 'Blitz' : lichessPerfs.rapid?.rating ? 'Rapid' : null;

  const enter = (i) => ({
    transitionDelay: `${i * 45}ms`,
    opacity: mounted ? 1 : 0,
    transform: mounted ? 'translateY(0)' : 'translateY(12px)',
  });

  return (
    <div className="min-h-full bg-[#F5F7FB]" data-testid="student-dashboard">
      {/* HEADER — identidad Chess4Kings (navy + ámbar) */}
      <div
        className="relative overflow-hidden bg-[#002537] bg-cover bg-[center_75%] text-white"
        style={{
          backgroundImage: "url(/branding/banner-chess4kings-teal.png)",
        }}
      >
        <div className="relative max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-10 sm:pt-10 sm:pb-14">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-9 h-9 rounded-xl bg-white/10 backdrop-blur grid place-items-center ring-1 ring-white/15">
              <Crown className="w-5 h-5 text-amber-300" />
            </div>
            <span className="font-display font-extrabold tracking-tight">Mi Chess4Kings</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight" data-testid="student-greeting">
            Hola, {user?.first_name || 'campeón'} <span className="text-amber-400">♟</span>
          </h1>
          <p className="text-white/70 mt-2 text-sm sm:text-base">¿Listo para tu próxima jugada? Elige por dónde empezar.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-0 pb-12 relative">
        {/* 1) NAVEGACIÓN PRINCIPAL */}
        <div className="mb-3">
          <h2 className="font-display font-bold text-lg text-slate-800">¿Qué quieres hacer?</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {TOOLS.map((t, i) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.key}
                to={t.to}
                data-testid={`student-tool-${t.key}`}
                style={enter(i)}
                className={`group relative flex flex-col rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-sm transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-xl ${t.glow} hover:border-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400`}
              >
                <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl grid place-items-center mb-3 sm:mb-4 transition-transform duration-300 group-hover:scale-105 ${t.tile}`}>
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
                </div>
                <div className="font-display font-bold text-base sm:text-lg text-slate-800 leading-tight">{t.label}</div>
                <div className="text-xs sm:text-sm text-slate-500 mt-0.5 leading-snug">{t.desc}</div>
                <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-slate-400 group-hover:text-amber-600 transition-colors">
                  Abrir <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* 2) PROGRESO / GAMIFICACIÓN — secundario */}
        <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white p-6 sm:p-8 mt-6 sm:mt-8 relative overflow-hidden" data-testid="student-progress">
          <div className="absolute -right-10 -top-10 w-56 h-56 rounded-full bg-amber-500/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-5">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-xs uppercase tracking-widest text-white/60 font-bold">Mi progreso</span>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5 sm:gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">Nivel</div>
                <div className="font-display text-3xl sm:text-4xl font-extrabold flex items-center gap-2" data-testid="student-level">
                  {level}<Sparkles className="w-5 h-5 text-amber-400" />
                </div>
                <div className="text-[11px] text-white/60 mt-1 font-mono">{points} / {nextLevelPts} XP</div>
                <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${progress}%` }} />
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">Puntos</div>
                <div className="font-display text-3xl sm:text-4xl font-extrabold flex items-center gap-2" data-testid="student-points">
                  <Star className="w-5 h-5 text-amber-400" />{points}
                </div>
                <div className="text-[11px] text-white/60 mt-1">Chess Points</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">Racha</div>
                <div className="font-display text-3xl sm:text-4xl font-extrabold flex items-center gap-2" data-testid="student-streak">
                  <Flame className="w-5 h-5 text-orange-400" />{s?.streak_days || 0}
                </div>
                <div className="text-[11px] text-white/60 mt-1">días seguidos</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">Rating</div>
                <div className="font-display text-3xl sm:text-4xl font-extrabold">{lichessRating || '—'}</div>
                <div className="text-[11px] text-white/60 mt-1">
                  {lichessRatingType ? `Lichess · ${lichessRatingType}` : 'Lichess'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 3) PENDIENTES / ACTIVIDAD — secundario */}
        <div className="grid md:grid-cols-3 gap-4 sm:gap-5 mt-6 sm:mt-8">
          <div className="md:col-span-2 space-y-4 sm:space-y-5">
            <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6" data-testid="student-assignments-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-base sm:text-lg text-slate-800">Tareas pendientes</h3>
                <Link to="/student/assignments" className="text-xs font-semibold text-blue-600 inline-flex items-center gap-0.5 hover:text-blue-700">Ver todas <ChevronRight className="w-3.5 h-3.5" /></Link>
              </div>
              {assignments.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center">No tienes tareas pendientes. ¡Bien hecho!</div>
              ) : assignments.map(a => (
                <Link key={a.id} to="/student/assignments" data-testid={`assignment-${a.id}`} className="flex items-center gap-3 py-2.5 border-b last:border-0 border-slate-100 hover:bg-slate-50 -mx-2 px-2 rounded-lg transition-colors">
                  <div className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 grid place-items-center shrink-0"><ClipboardList className="w-4 h-4" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-slate-800 truncate">{a.title}</div>
                    <div className="text-xs text-slate-400">{a.type} · {a.points} pts</div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                </Link>
              ))}
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6" data-testid="student-courses-panel">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-base sm:text-lg text-slate-800">Continúa aprendiendo</h3>
                <Link to="/student/courses" className="text-xs font-semibold text-blue-600 inline-flex items-center gap-0.5 hover:text-blue-700">Ver todos <ChevronRight className="w-3.5 h-3.5" /></Link>
              </div>
              {courses.length === 0 ? (
                <div className="text-sm text-slate-400 py-6 text-center">Aún no tienes cursos.</div>
              ) : (
                <div className="grid sm:grid-cols-3 gap-4">
                  {courses.map(c => (
                    <Link key={c.id} to={`/student/courses/${c.id}`} data-testid={`course-${c.id}`} className="rounded-xl overflow-hidden border border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all">
                      <div className="h-24 bg-slate-100" style={{ backgroundImage: `url(${c.cover_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                      <div className="p-3">
                        <div className="font-semibold text-sm text-slate-800 line-clamp-1">{c.title}</div>
                        <div className="text-xs text-slate-400 line-clamp-1">{c.description}</div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4 sm:space-y-5">
            <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6" data-testid="student-achievements-panel">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display font-bold text-base sm:text-lg text-slate-800">Últimos logros</h3>
                <Link to="/student/achievements" className="text-xs font-semibold text-blue-600 inline-flex items-center gap-0.5 hover:text-blue-700">Ver <ChevronRight className="w-3.5 h-3.5" /></Link>
              </div>
              {achs.length === 0 ? (
                <div className="text-sm text-slate-400 py-4 text-center">Resuelve ejercicios para desbloquear logros.</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {achs.map(a => (
                    <div key={a.id} className="rounded-xl bg-slate-50 p-3 text-center">
                      <div className="w-10 h-10 mx-auto rounded-full bg-amber-400 text-white grid place-items-center mb-1"><Trophy className="w-5 h-5" /></div>
                      <div className="text-xs font-semibold text-slate-700 line-clamp-1">{a.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-5 sm:p-6" data-testid="student-notifications-panel">
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-4 h-4 text-slate-400" />
                <h3 className="font-display font-bold text-base sm:text-lg text-slate-800">Notificaciones</h3>
              </div>
              {notes.length === 0 ? (
                <div className="text-sm text-slate-400">Sin novedades por ahora.</div>
              ) : notes.map(n => (
                <div key={n.id} className="text-sm py-2 border-b last:border-0 border-slate-100">
                  <div className="text-slate-700">{n.message}</div>
                  <div className="text-xs text-slate-400">{new Date(n.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
